import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, Package, Shield } from 'lucide-react';
import { assetService } from '../../services/assetService';
import { userProfileService } from '../../services/userProfileService';

export default function AssetManagementDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewAsset = id === 'new';

  const [loading, setLoading] = useState(!isNewAsset);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [userCurrency, setUserCurrency] = useState('USD');

  const [formData, setFormData] = useState({
    assetName: '',
    assetType: 'computer',
    assetValue: '',
    location: 'in_office',
    assignedStaffMember: '',
    serialNumber: '',
    warrantyExpiry: '',
    specifications: {},
    isInsured: false,
    purchaseDate: '',
    notes: ''
  });

  const [specificationFields, setSpecificationFields] = useState([
    { key: '', value: '' }
  ]);

  useEffect(() => {
    if (!isNewAsset) {
      loadAsset();
    }
    loadUserCurrency();
  }, [id]);

  const loadUserCurrency = async () => {
    try {
      const profile = await userProfileService?.getCurrentProfile();
      setUserCurrency(profile?.currency || 'USD');
    } catch (err) {
      console.error('Error loading user currency:', err);
      setUserCurrency('USD');
    }
  };

  const loadAsset = async () => {
    try {
      setLoading(true);
      const asset = await assetService?.getById(id);
      setFormData(asset);
      
      // Convert specifications object to array for editing
      if (asset?.specifications && Object.keys(asset?.specifications)?.length > 0) {
        const specs = Object.entries(asset?.specifications)?.map(([key, value]) => ({
          key,
          value
        }));
        setSpecificationFields(specs);
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSpecificationChange = (index, field, value) => {
    const newSpecs = [...specificationFields];
    newSpecs[index][field] = value;
    setSpecificationFields(newSpecs);
  };

  const addSpecificationField = () => {
    setSpecificationFields([...specificationFields, { key: '', value: '' }]);
  };

  const removeSpecificationField = (index) => {
    if (specificationFields?.length > 1) {
      const newSpecs = specificationFields?.filter((_, i) => i !== index);
      setSpecificationFields(newSpecs);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validate required fields
    if (!formData?.assetName?.trim()) {
      setError('Asset name is required');
      return;
    }
    if (!formData?.assignedStaffMember?.trim()) {
      setError('Assigned staff member is required');
      return;
    }
    if (!formData?.assetValue || parseFloat(formData?.assetValue) <= 0) {
      setError('Asset value must be greater than 0');
      return;
    }

    try {
      setSaving(true);

      // Convert specifications array back to object
      const specifications = specificationFields?.reduce((acc, spec) => {
        if (spec?.key?.trim()) {
          acc[spec.key] = spec?.value;
        }
        return acc;
      }, {});

      const assetData = {
        ...formData,
        specifications,
        assetValue: parseFloat(formData?.assetValue),
        currency: userCurrency // Use currency from user profile
      };

      if (isNewAsset) {
        await assetService?.create(assetData);
        setSuccessMessage('Asset created successfully!');
        setTimeout(() => navigate('/asset-management-overview'), 1500);
      } else {
        await assetService?.update(id, assetData);
        setSuccessMessage('Asset updated successfully!');
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      await assetService?.delete(id);
      setSuccessMessage('Asset deleted successfully!');
      setTimeout(() => navigate('/asset-management-overview'), 1500);
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading asset details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/asset-management-overview')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isNewAsset ? 'Add New Asset' : 'Asset Details'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isNewAsset ? 'Create a new asset entry' : 'View and edit asset information'}
            </p>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <Package className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Basic Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asset Name *
                </label>
                <input
                  type="text"
                  name="assetName"
                  value={formData?.assetName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., MacBook Pro 16"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asset Type *
                </label>
                <select
                  name="assetType"
                  value={formData?.assetType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="computer">Computer</option>
                  <option value="furniture">Furniture</option>
                  <option value="equipment">Equipment</option>
                  <option value="software">Software</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asset Value ({userCurrency}) *
                </label>
                <input
                  type="number"
                  name="assetValue"
                  value={formData?.assetValue}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">Currency from your account settings</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Staff Member *
                </label>
                <input
                  type="text"
                  name="assignedStaffMember"
                  value={formData?.assignedStaffMember}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <select
                  name="location"
                  value={formData?.location}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="in_office">In Office</option>
                  <option value="remote">Remote</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Serial Number
                </label>
                <input
                  type="text"
                  name="serialNumber"
                  value={formData?.serialNumber}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Date
                </label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={formData?.purchaseDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Warranty Expiry
                </label>
                <input
                  type="date"
                  name="warrantyExpiry"
                  value={formData?.warrantyExpiry}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isInsured"
                    checked={formData?.isInsured}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Asset is Insured
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Specifications</h2>
              <button
                type="button"
                onClick={addSpecificationField}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + Add Field
              </button>
            </div>

            <div className="space-y-4">
              {specificationFields?.map((spec, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={spec?.key}
                    onChange={(e) => handleSpecificationChange(index, 'key', e?.target?.value)}
                    placeholder="Key (e.g., Processor)"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={spec?.value}
                      onChange={(e) => handleSpecificationChange(index, 'value', e?.target?.value)}
                      placeholder="Value (e.g., M1 Max)"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {specificationFields?.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSpecificationField(index)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Notes</h2>
            <textarea
              name="notes"
              value={formData?.notes}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Additional notes about this asset..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            {!isNewAsset && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-5 h-5" />
                Delete Asset
              </button>
            )}
            <div className={`flex gap-4 ${isNewAsset ? 'ml-auto' : ''}`}>
              <button
                type="button"
                onClick={() => navigate('/asset-management-overview')}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : isNewAsset ? 'Create Asset' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}